import { useCallback, useEffect, useRef } from 'react';

/**
 * Raccourcis clavier des listes, complément de `useHotkey` (patterns.tsx) pour la navigation
 * ligne par ligne (branchés dans `DataTable`) :
 * - « / » place le curseur dans la recherche de la page ;
 * - « j » / « k » (ou flèches bas / haut) passent à la ligne suivante / précédente du tableau ;
 * - « Entrée » ouvre la ligne sélectionnée (son premier lien) ;
 * - « Échap » quitte la recherche.
 * Inactifs pendant une saisie (sauf Échap) et avec Ctrl, Cmd ou Alt.
 */

/**
 * L'utilisateur tape-t-il dans un champ ? Partagé avec `useHotkey` (patterns.tsx) : les
 * raccourcis sans Ctrl/⌘ sont alors ignorés.
 */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/** Lignes navigables d'un conteneur (corps des tableaux). */
const rowsOf = (root: HTMLElement): HTMLTableRowElement[] =>
  [...root.querySelectorAll<HTMLTableRowElement>('table tbody tr')].filter(
    (r) => r.querySelector('a[href], button') !== null,
  );

/** Listes équipées, dans l'ordre de montage. */
const owners: { root: { current: HTMLElement | null } }[] = [];

/** Prochain index sélectionné (bornes respectées), `-1` si aucune ligne. */
export function nextIndex(current: number, count: number, step: 1 | -1): number {
  if (count === 0) return -1;
  if (current < 0) return step === 1 ? 0 : count - 1;
  return Math.min(count - 1, Math.max(0, current + step));
}

/**
 * Active les raccourcis pour la liste contenue dans l'élément retourné (`ref`). Un seul
 * gestionnaire par page : le premier conteneur monté.
 */
export function useListKeyboard(): (el: HTMLElement | null) => void {
  const root = useRef<HTMLElement | null>(null);
  const selected = useRef(-1);
  const ref = useCallback((el: HTMLElement | null) => {
    root.current = el;
  }, []);
  useEffect(() => {
    // Plusieurs listes sur une page (onglets, liste dans un conteneur déjà équipé) : seule la
    // première liste visible réagit, pour ne jamais traiter une touche deux fois.
    const owner = { root };
    owners.push(owner);
    const select = (rows: HTMLTableRowElement[], i: number): void => {
      rows.forEach((r, j) => {
        r.classList.toggle('o-row--selected', j === i);
        if (j === i) r.setAttribute('aria-selected', 'true');
        else r.removeAttribute('aria-selected');
      });
      rows[i]?.scrollIntoView({ block: 'nearest' });
      selected.current = i;
    };
    const onKey = (e: KeyboardEvent): void => {
      const el = root.current;
      if (owners.find((o) => o.root.current?.offsetParent != null) !== owner) return;
      if (el === null || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape' && isTyping(e.target)) {
        (e.target as HTMLElement).blur();
        return;
      }
      if (isTyping(e.target)) return;
      if (e.key === '/') {
        const search =
          document.querySelector<HTMLInputElement>('main input[type="search"]') ??
          document.querySelector<HTMLInputElement>('input[type="search"]');
        if (search !== null) {
          e.preventDefault();
          search.focus();
          search.select();
        }
        return;
      }
      const rows = rowsOf(el);
      if (e.key === 'j' || e.key === 'ArrowDown' || e.key === 'k' || e.key === 'ArrowUp') {
        if (rows.length === 0) return;
        // Flèches : seulement une fois une ligne choisie (le défilement normal reste possible).
        if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && selected.current < 0) return;
        e.preventDefault();
        const step = e.key === 'j' || e.key === 'ArrowDown' ? 1 : -1;
        select(rows, nextIndex(selected.current, rows.length, step));
        return;
      }
      if (e.key === 'Enter' && selected.current >= 0) {
        const target = rows[selected.current]?.querySelector<HTMLElement>('a[href], button');
        if (target !== undefined && target !== null) {
          e.preventDefault();
          target.click();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      owners.splice(owners.indexOf(owner), 1);
    };
  }, []);
  return ref;
}
